import React from 'react';
import { lazy } from 'react-loosely-lazy';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants';
import { useIsTargetedForRaisingAwarenessOfImporter } from '../../controllers';
import { ImportLink } from './import-link';
import messages from './messages';
import type { ImportWorkButtonInternal as ImportWorkButtonInternalType } from './index';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyImportWorkButtonInternal = lazy<typeof ImportWorkButtonInternalType>(
	() =>
		import(/* webpackChunkName: "async-onboarding-invites-import-work-button" */ './index').then(
			({ ImportWorkButtonInternal }) => ImportWorkButtonInternal,
		),
	{ ssr: false },
);

const ImportWorkButtonWithTargeting = () => {
	const isTargeted = useIsTargetedForRaisingAwarenessOfImporter();

	return isTargeted ? <LazyImportWorkButtonInternal /> : null;
};

const ImportLinkFallback = () => {
	const { formatMessage } = useIntl();
	const label = formatMessage(messages.importButtonText);

	const attributes = {
		importerType: 'JIM_DEFAULT',
		importButtonFallback: true,
	};

	const onClick = (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, 'importWork', attributes);
	};
	return <ImportLink label={label} onClick={onClick} attributes={attributes} />;
};

export const ImportWorkButton = () => (
	<JSErrorBoundary
		id="importWorkButton"
		packageName={PACKAGE_NAME}
		teamName={TEAM_NAME}
		fallback={() => <ImportLinkFallback />}
	>
		<ImportWorkButtonWithTargeting />
	</JSErrorBoundary>
);
